import React, { useEffect, useMemo, useRef, useState } from 'react';
import { debounce } from 'lodash';
import { connect } from 'react-redux';
import ContactMobileNumberVerificationActions, {
   getCurrentVerification,
   getContactMobileNumber,
   getValidateMobileNumberResult,
   getVerifyResult,
   getSendOtpResult,
   getResendOtpResult,
   isSendingOtp,
   isResendingOtp,
   isCancelling,
   isVerifyingAndSaving,
   isFetchingCurrentVerification,
   isFetchingContactMobileNumber,
   isFetchingContactMobileNumberSuccess,
   isValidatingMobileNumber,
} from 'pgdb-data-layer/lib/Redux/ContactMobileNumberVerificationRedux';
import { checkValidNumber } from 'pgdb-data-layer/lib/Helpers/Profile';
import MobileNumberTextField, {
   ViewState,
   RESEND_COOLDOWN_SECONDS,
   VERIFICATION_CODE_LENGTH,
   remainingCooldownSeconds,
   getLockoutSeconds,
} from '../MobileNumberTextField';
import { getToken } from '../../../Session/SessionRedux';

// How long the verified confirmation stays up before dropping back to the read-only view.
const SUCCESS_DISPLAY_MS = 2500;

const MobileNumberTextFieldContainer = ({
   token,
   currentVerificationData,
   contactMobileNumberData,
   validateMobileNumberData,
   verifyMobileNumberData,
   sendOtpData,
   resendOtpData,
   isSendingOtp,
   isResendingOtp,
   isCancelling,
   isVerifyingAndSaving,
   isFetchingCurrentVerification,
   isFetchingContactMobileNumber,
   hasFetchedContactMobileNumber,
   isValidatingMobileNumber,
   sendOtp,
   resendOtp,
   cancel,
   getCurrentVerification,
   getContactMobileNumber,
   verifyAndSave,
   validateMobileNumber,
   clearValidateAndVerifyResults,
   sendOtpReset,
   resendOtpReset,
   inputValid,
   onVerified,
   onMobileNumberChange,
   onValidityChange,
}) => {
   const [viewState, setViewState] = useState<ViewState>(ViewState.INITIAL);
   const [initialMobileNumber, setInitialMobileNumber] = useState(
      contactMobileNumberData.mobileNumber || ''
   );
   const [mobileNumber, setMobileNumber] = useState(
      contactMobileNumberData.mobileNumber || ''
   );
   const [verificationCode, setVerificationCode] = useState('');
   const [resendCooldown, setResendCooldown] = useState(0);
   const [lockoutSeconds, setLockoutSeconds] = useState(0);

   // An in-flight session is resumed once per mount. Without this guard a stale
   // currentVerification left in the store after a cancel would drag the user back
   // into VALIDATE (the failure path of GET /current does not clear the cached data).
   const hasResumedRef = useRef(false);

   const clearResults = () => {
      clearValidateAndVerifyResults();
      sendOtpReset();
      resendOtpReset();
   };

   useEffect(() => {
      if (token) {
         getCurrentVerification(token);
         getContactMobileNumber(token);
      }
   }, [token]);

   // Only mirror the saved contact number while no change is in flight. The two mount
   // fetches resolve in either order, so without this guard a late GET
   // /contact-mobile-number would overwrite the pending number a resumed session just set.
   useEffect(() => {
      if (
         viewState === ViewState.EDIT ||
         viewState === ViewState.VALIDATE ||
         viewState === ViewState.LOCKED
      ) {
         return;
      }

      setMobileNumber(contactMobileNumberData.mobileNumber || '');
      setInitialMobileNumber(contactMobileNumberData.mobileNumber || '');
   }, [contactMobileNumberData.mobileNumber]);

   // The read-only view has to say why an unverified number needs attention, and only the
   // API can tell a number linked to another account from one that is merely unverified.
   // Keyed on viewState rather than firing once on mount because handleCancel clears the
   // result on the way back from EDIT, which would leave the view with nothing to show.
   useEffect(() => {
      if (viewState !== ViewState.INITIAL) return;
      if (!token) return;

      const savedNumber = contactMobileNumberData.mobileNumber;
      if (!savedNumber || contactMobileNumberData.mobileNumberVerifiedDate)
         return;

      validateMobileNumber(token, savedNumber);
   }, [
      viewState,
      token,
      contactMobileNumberData.mobileNumber,
      contactMobileNumberData.mobileNumberVerifiedDate,
   ]);

   const debouncedValidateMobileNumber = useMemo(
      () =>
         debounce((value: string) => validateMobileNumber(token, value), 500),
      [token, validateMobileNumber]
   );

   useEffect(() => {
      return () => {
         debouncedValidateMobileNumber.cancel();
      };
   }, [debouncedValidateMobileNumber]);

   // Resume a verification the practitioner already started.
   useEffect(() => {
      if (hasResumedRef.current) return;

      const pendingNumber =
         currentVerificationData && currentVerificationData.mobileNumber;
      if (!pendingNumber) return;

      hasResumedRef.current = true;

      // The pending number is the one awaiting an OTP, not the number saved on the
      // contact — the verify screen has to echo the number the code was texted to.
      setMobileNumber(pendingNumber);

      const lockout = getLockoutSeconds(currentVerificationData);
      if (lockout !== null) {
         setLockoutSeconds(lockout);
         setViewState(ViewState.LOCKED);
         return;
      }

      setResendCooldown(
         remainingCooldownSeconds(currentVerificationData.startDate)
      );
      setViewState(ViewState.VALIDATE);
   }, [currentVerificationData]);

   // Gated on EDIT because that is the only state that can send an OTP. On a remount
   // a successful sendOtpResult still sitting in the store would otherwise re-fire and
   // stamp a fresh 60s cooldown over the real one the resume effect just calculated.
   useEffect(() => {
      if (viewState !== ViewState.EDIT) return;

      if (sendOtpData && sendOtpData.isSuccess) {
         setResendCooldown(RESEND_COOLDOWN_SECONDS);
         setViewState(ViewState.VALIDATE);
         return;
      }

      // A send refused because an earlier session is still locked out.
      const lockout = getLockoutSeconds(sendOtpData);
      if (lockout !== null) {
         setLockoutSeconds(lockout);
         setViewState(ViewState.LOCKED);
      }
   }, [viewState, sendOtpData]);

   // A resend replaces the session server-side, so the attempt count and any stale
   // "code is invalid" message from the previous code no longer apply.
   useEffect(() => {
      if (viewState !== ViewState.VALIDATE) return;

      if (resendOtpData && resendOtpData.isSuccess) {
         setVerificationCode('');
         setResendCooldown(RESEND_COOLDOWN_SECONDS);
         clearValidateAndVerifyResults();
         return;
      }

      const lockout = getLockoutSeconds(resendOtpData);
      if (lockout !== null) {
         setVerificationCode('');
         setLockoutSeconds(lockout);
         setViewState(ViewState.LOCKED);
      }
   }, [viewState, resendOtpData]);

   useEffect(() => {
      // Gated on VALIDATE for the same reason as sendOtp: a stale verifyResult in the
      // store must not re-announce a success (and re-fire onVerified) on remount.
      if (viewState !== ViewState.VALIDATE) return;

      // isSuccess is absent only on the empty initial/reset state.
      if (
         !verifyMobileNumberData ||
         verifyMobileNumberData.isSuccess === undefined
      )
         return;

      if (verifyMobileNumberData.isSuccess) {
         setVerificationCode('');
         setResendCooldown(0);
         setViewState(ViewState.SUCCESS);
         getContactMobileNumber(token);
         if (onVerified) onVerified(verifyMobileNumberData.mobileNumber);
         return;
      }

      // The API sends lockoutSecondsRemaining the moment the final attempt is spent,
      // so no extra round trip is needed to start the countdown.
      const lockout = getLockoutSeconds(verifyMobileNumberData);
      if (lockout !== null) {
         setVerificationCode('');
         setLockoutSeconds(lockout);
         setViewState(ViewState.LOCKED);
      }
      // Any other failure keeps the user on VALIDATE with the message from the API.
   }, [viewState, verifyMobileNumberData]);

   // Lets a host form (e.g. the profile save) source the number it submits from the
   // confirmed value here rather than keeping its own copy, so it can never send a
   // stale number that clobbers a verification this container just completed.
   // Gated on the fetch having actually succeeded at least once — contactMobileNumberData
   // defaults to {} before then, and reporting that default up as '' would blow away
   // whatever value the host already had hydrated from its own initial load.
   useEffect(() => {
      if (onMobileNumberChange && hasFetchedContactMobileNumber) {
         onMobileNumberChange(contactMobileNumberData.mobileNumber || '');
      }
   }, [contactMobileNumberData.mobileNumber, hasFetchedContactMobileNumber]);

   // Lets a host form block its own save while a mobile number change is mid-flight
   // (so it never submits alongside a half-finished/unverified edit), while the
   // confirmed number isn't a valid one, or while it has never been OTP-verified —
   // this is an MFA-adjacent field, so an unverified number (including a correctly
   // formatted legacy number saved before this flow existed) must not silently pass.
   useEffect(() => {
      if (onValidityChange) {
         const isSettled =
            viewState === ViewState.INITIAL || viewState === ViewState.SUCCESS;
         const isValidNumber = !!checkValidNumber(
            contactMobileNumberData.mobileNumber
         );
         const isVerified = !!contactMobileNumberData.mobileNumberVerifiedDate;
         onValidityChange(isSettled && isValidNumber && isVerified);
      }
   }, [
      viewState,
      contactMobileNumberData.mobileNumber,
      contactMobileNumberData.mobileNumberVerifiedDate,
   ]);

   useEffect(() => {
      if (resendCooldown <= 0) return undefined;

      const tick = setTimeout(
         () => setResendCooldown(seconds => seconds - 1),
         1000
      );
      return () => clearTimeout(tick);
   }, [resendCooldown]);

   // The locked view has no actions, so the countdown itself is the way out: once the
   // lockout window closes, drop back to the read-only view so a new code can be requested.
   useEffect(() => {
      if (viewState !== ViewState.LOCKED) return undefined;

      if (lockoutSeconds <= 0) {
         clearResults();
         setViewState(ViewState.INITIAL);
         return undefined;
      }

      const tick = setTimeout(() => setLockoutSeconds(s => s - 1), 1000);
      return () => clearTimeout(tick);
   }, [viewState, lockoutSeconds]);

   useEffect(() => {
      if (viewState !== ViewState.SUCCESS) return undefined;

      const timer = setTimeout(() => {
         clearValidateAndVerifyResults();
         setViewState(ViewState.INITIAL);
      }, SUCCESS_DISPLAY_MS);
      return () => clearTimeout(timer);
   }, [viewState]);

   const handleEdit = () => {
      setMobileNumber(contactMobileNumberData.mobileNumber || '');
      setVerificationCode('');
      setLockoutSeconds(0);
      clearResults();
      setViewState(ViewState.EDIT);
   };

   const handleCancel = () => {
      // Only VALIDATE has a live session worth expiring. Cancelling from EDIT would
      // hit the API with nothing to cancel, and cancelling from LOCKED would cut the
      // server-side lockout window short.
      if (viewState === ViewState.VALIDATE) {
         cancel(token);
      }

      setMobileNumber(contactMobileNumberData.mobileNumber || '');
      setVerificationCode('');
      setResendCooldown(0);
      setLockoutSeconds(0);
      clearResults();
      setViewState(ViewState.INITIAL);
   };

   const handleMobileNumberChange = (
      e: React.ChangeEvent<HTMLInputElement>
   ) => {
      const { value } = e.target;
      setMobileNumber(value);
      debouncedValidateMobileNumber(value);
   };

   const handleVerificationCodeChange = (
      e: React.ChangeEvent<HTMLInputElement>
   ) => {
      const digitsOnly = e.target.value
         .replace(/\D/g, '')
         .slice(0, VERIFICATION_CODE_LENGTH);
      setVerificationCode(digitsOnly);
   };

   const handleSave = () => {
      setVerificationCode('');
      sendOtp(token, mobileNumber);
   };

   const handleVerify = () => {
      if (verificationCode.length !== VERIFICATION_CODE_LENGTH) return;
      if (isVerifyingAndSaving) return;
      verifyAndSave(token, verificationCode);
   };

   const handleResend = () => {
      if (resendCooldown > 0 || isResendingOtp) return;
      resendOtp(token);
   };

   return (
      <MobileNumberTextField
         viewState={viewState}
         verificationCode={verificationCode}
         mobileNumberDetails={{ ...contactMobileNumberData, mobileNumber }}
         initialMobileNumber={initialMobileNumber}
         validationDetails={validateMobileNumberData}
         sendOtpData={sendOtpData}
         resendOtpData={resendOtpData}
         verifyDetails={verifyMobileNumberData}
         resendCooldown={resendCooldown}
         lockoutSeconds={lockoutSeconds}
         isValidatingMobileNumber={isValidatingMobileNumber}
         isSendingOtp={isSendingOtp}
         isResendingOtp={isResendingOtp}
         isCancelling={isCancelling}
         isVerifyingAndSaving={isVerifyingAndSaving}
         isFetchingCurrentVerification={isFetchingCurrentVerification}
         isFetchingContactMobileNumber={isFetchingContactMobileNumber}
         inputValid={inputValid}
         onEdit={handleEdit}
         onCancel={handleCancel}
         onMobileNumberChange={handleMobileNumberChange}
         onSave={handleSave}
         onVerificationCodeChange={handleVerificationCodeChange}
         onVerify={handleVerify}
         onResend={handleResend}
      />
   );
};

const mapStateToProps = state => ({
   token: getToken(state),
   currentVerificationData: getCurrentVerification(state),
   contactMobileNumberData: getContactMobileNumber(state),
   validateMobileNumberData: getValidateMobileNumberResult(state),
   verifyMobileNumberData: getVerifyResult(state),
   sendOtpData: getSendOtpResult(state),
   resendOtpData: getResendOtpResult(state),
   hasFetchedContactMobileNumber: isFetchingContactMobileNumberSuccess(state),
   isSendingOtp: isSendingOtp(state),
   isResendingOtp: isResendingOtp(state),
   isCancelling: isCancelling(state),
   isVerifyingAndSaving: isVerifyingAndSaving(state),
   isFetchingCurrentVerification: isFetchingCurrentVerification(state),
   isFetchingContactMobileNumber: isFetchingContactMobileNumber(state),
   isValidatingMobileNumber: isValidatingMobileNumber(state),
});

const mapDispatchToProps = {
   sendOtp: ContactMobileNumberVerificationActions.sendOtpRequest,
   resendOtp: ContactMobileNumberVerificationActions.resendOtpRequest,
   cancel: ContactMobileNumberVerificationActions.cancelRequest,
   getCurrentVerification:
      ContactMobileNumberVerificationActions.getCurrentVerificationRequest,
   getContactMobileNumber:
      ContactMobileNumberVerificationActions.getContactMobileNumberRequest,
   verifyAndSave: ContactMobileNumberVerificationActions.verifyAndSaveRequest,
   validateMobileNumber:
      ContactMobileNumberVerificationActions.validateMobileNumberRequest,
   clearValidateAndVerifyResults:
      ContactMobileNumberVerificationActions.clearValidateAndVerifyResults,
   sendOtpReset: ContactMobileNumberVerificationActions.sendOtpReset,
   resendOtpReset: ContactMobileNumberVerificationActions.resendOtpReset,
};

export default connect(
   mapStateToProps,
   mapDispatchToProps
)(MobileNumberTextFieldContainer);
